import { isTiersProductVariant,
  isTiersVolumeProduct, isUsageProduct,
  UsageProductPriceEstimator } from '@weft/services';
import type { UsageProductInput } from '@weft/types';
import { DEFAULT_CURRENCY, type BillingInterval,
  type PriceTier,
  type TiersProductVariant,
  type UsageProduct } from '@weft/types';
import { isNotNullOrUndefined, raise } from '@weft/utils';
import React, { useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { VayuTextField } from '../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useCreatePlanContext } from '../../../../../../contexts/create-plan-context';
import { useScrollIntoViewOnMount } from '../../../../../../hooks/use-scroll-to';
import { getDefaultTiersUsageProductVariant } from '../../../create-plan-page-consts/add-item-button-consts';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { PlanChargesContainer } from '../../PlanChargesContainer';
import ProductDotMenuDropdown from '../../ProductDotMenu';
import { UsageProductGroupDialogForm } from './UsageProductGroupDialogForm';
import { UsageProductVariantHeader } from './UsageProductVariantHeader';
import { UsageProductVariantSummary } from './UsageProductVariantSummary';
import { UsageProductVariantTable } from './UsageProductVariantTable';

type TiersUsageChargeEditProps = {
  billingInterval: BillingInterval;
};

export const TiersUsageChargeEdit: React.FC<TiersUsageChargeEditProps> = ({ billingInterval }) => {
  const { item: product, itemIndex: productIndex, currency } = useChargeContext<UsageProduct>();
  const { register, getValues, formState: { errors }, setValue } = useFormContext<PlanInputForm>();
  const [isProductGroupDialogOpen, setIsProductGroupDialogOpen] = useState(false);
  const { formUtils: { isFieldEditable } } = useCreatePlanContext();

  useScrollIntoViewOnMount(`create-product-${productIndex}`);

  const productNameRegister = register(`usageProducts.${productIndex}.name`, {
    value: product.name,
  });

  const usageProductPriceEstimator = new UsageProductPriceEstimator();
  const price = usageProductPriceEstimator.estimate(product, billingInterval);

  const handleConvertToGroup = () => {
    setIsProductGroupDialogOpen(true);
  };

  const addVariant = () => {
    const newVariant = getDefaultTiersUsageProductVariant();

    const updatedProductVariants = [...product.variants, newVariant];
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  };

  const isGroupedProduct = product.variants.length > 1;
  const isVolumeProduct = isTiersVolumeProduct(product);

  function handleGroupDelete() {
    const [firstVariant] = product.variants;
    setValue(`usageProducts.${productIndex}.name`, undefined);
    setValue(`usageProducts.${productIndex}.variants`, [firstVariant]);
  }

  return (
    <div className={twMerge('flex flex-col gap-y-4', isFieldEditable(`usageProducts.${productIndex}`) ? '' : 'pointer-events-none opacity-40')}>
      <PlanChargesContainer
        key={product.id}
        className="relative px-0 pb-0"
        id={`create-product-${productIndex}`}
      >
        {isGroupedProduct && (
          <div className="flex max-w-xs px-8 pt-8">
            <VayuTextField
              placeholder='Text here'
              {...productNameRegister}
              label="Group Name"
              defaultValue={getValues(productNameRegister.name) ?? ''}
              helperText={get(errors, productNameRegister.name)?.message}
              error={isNotNullOrUndefined(get(errors, productNameRegister.name))}
              focused
            />
          </div>
        )}

        <div>
          <div className='absolute right-3 top-3'>
            <ProductDotMenuDropdown
              isGrouped={isGroupedProduct}
              onCreateGroup={handleConvertToGroup}
              onDeleteGroup={handleGroupDelete}
            />
          </div>
          <UsageProductGroupDialogForm
            open={isProductGroupDialogOpen}
            onClose={() => setIsProductGroupDialogOpen(false)}
            onCreateClick={() => !isGroupedProduct && addVariant()}
            productIndex={productIndex}
          />

          <div>
            {product.variants.map((variant, variantIndex) => {
              if (!isTiersProductVariant(variant)) {
                raise('Invalid product variant type');
              }

              return (
                <div
                  key={variant.id}
                  className="even:bg-product-bk pt-10"
                >
                  <UsageProductVariantHeader
                    productVariant={variant}
                    productVariantIndex={variantIndex}
                    productIndex={productIndex}
                    costEstimation={price}
                    className="px-8"
                    isEditing
                    product={product}
                  />
                  <UsageProductVariantTable
                    key={variant.id}
                    isEditing
                    isVolumeProduct={isVolumeProduct}
                    isFlatRate={variant.pricing.priceConfiguration?.isFlatRate ?? false}
                    isRevShareProduct={isUsageProduct<UsageProductInput>(product) && product.isRevShare}
                    tiers={getTiers(variant)}
                    productIndex={productIndex}
                    productVariantIndex={variantIndex}
                    currency={DEFAULT_CURRENCY}
                  />
                </div>
              );
            })}
          </div>
          <UsageProductVariantSummary  
            editable
            isEditing
            product={product}
            productIndex={productIndex}
            enterEditMode={() => {}}
            isGroupedProduct={isGroupedProduct}
            currency={currency}
          />
        </div>
      </PlanChargesContainer>
    </div>
  );
};

function getTiers(productVariant: TiersProductVariant): Partial<PriceTier>[] {
  const tiers = productVariant.pricing?.priceConfiguration?.tiers as Partial<PriceTier>[];

  return tiers ?? [] as Partial<PriceTier>[];
}